import axios from 'axios';
import { AdminCompanyDto, AdminUserDto, AppResponse, AuditEventDto, ConfigurationBackupContentDto, ConfigurationBackupDto, ConfigurationDiffDto, ControlCenterFeatureFlags, CreateTenantRequest, DocumentTemplateDto, LoginDto, MeshDeviceDto, MeshPowerAction, MeshPowerOperationDto, OperationsCheckupDetailsDto, OperationsCheckupDto, OperationsDashboardDto, OverviewDto, QueueSettingDto, QuickCreateTenantRequest, SystemMaintenanceDto, SystemStatusMode, TenantCreatedDto, TenantDetailsDto, TenantDto, TenantFeaturesDto, TerminalDiagnosticsDto, TerminalDto, TerminalHealthDto, TerminalLogEntry, RabbitMQOverview, RabbitMQQueue, RabbitMQConnection, S3Bucket, S3ObjectList, S3Stats, AnalyticsDay, TenantActivity, TerminalUptime, MedicLoad, UpdateAdminCompanyRequest, UpdateAdminTerminalRequest, UpdateAdminUserRequest, UpdateTenantRequest } from '../models';
import type { MeshCommandDefinitionDto, MeshCommandJobDto, MeshRemoteProtocol, MeshRemoteSessionDto } from '../models';
import type { MedscreenerBackupDto, SupportJobDto, SupportScenarioDto, TerminalExamDetailsDto, TerminalExamHistoryItemDto, TerminalExamHistorySearchDto, TerminalExamPrintOperationDto } from '../models';
import type { ActivityCenterDto, BluetoothTonometerDto, MeshDeviceHealthDto, MeshFleetSummaryDto, TonometerOperationDto, TonometerStateDto, TonometerTestDto } from '../models';
import type { AstralConnectionTestDto, AstralIntegrationDto, CreateEplCompanyRequest, EdoOperatorDto, EplTenantDto, SaveEplIntegrationRequest } from '../models';
import type { ConfigurationLiveDto, ConfigurationPreviewDto, ConfigurationTemplateDto } from '../models';
import type { AiAgentMessageDto, AiAgentReplyDto, AiAgentStatusDto } from '../models';

const tokenKey = 'telemed_platform_operator_token';
export const getToken = () => sessionStorage.getItem(tokenKey);
export const setToken = (token: string) => {
  sessionStorage.setItem(tokenKey, token);
  window.dispatchEvent(new Event('telemed-auth-changed'));
};
export const clearToken = () => {
  sessionStorage.removeItem(tokenKey);
  window.dispatchEvent(new Event('telemed-auth-changed'));
};

const api = axios.create({ baseURL: window.__controlCenterEnv?.apiUrl || '' });
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && !error?.config?.url?.includes('/accounts/login')) {
      clearToken();
      window.location.replace('/login');
    }
    return Promise.reject(error);
  },
);

export const login = (email: string, password: string) =>
  api.post<AppResponse<LoginDto>>('/api/v1/accounts/login', { email, password });

export const getControlCenterFeatureFlags = () =>
  api.get<AppResponse<ControlCenterFeatureFlags>>('/ops-api/features');

export const getAiAgentStatus = () =>
  api.get<AppResponse<AiAgentStatusDto>>('/ops-api/ai/status');

export const chatWithAiAgent = (messages: AiAgentMessageDto[], nodeId?: string, signal?: AbortSignal) =>
  api.post<AppResponse<AiAgentReplyDto>>('/ops-api/ai/chat', { messages, nodeId }, { signal });

export async function streamAiAgentChat(
  messages: AiAgentMessageDto[],
  nodeId: string | undefined,
  signal: AbortSignal,
  onStep: (step: import('../models').AiAgentStepDto) => void,
): Promise<AiAgentReplyDto> {
  const response = await fetch('/ops-api/ai/chat/stream', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
    body: JSON.stringify({ messages, nodeId }),
    signal,
  });
  if (!response.ok || !response.body) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload?.message || `HTTP ${response.status}`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: AiAgentReplyDto | null = null;
  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.trim()) continue;
      const event = JSON.parse(line);
      if (event.type === 'step') onStep(event.data);
      else if (event.type === 'done') result = event.data;
      else if (event.type === 'error') throw new Error(event.data?.message || 'Ошибка агента.');
    }
    if (done) break;
  }
  if (!result) throw new Error('Агент не вернул результат.');
  return result;
}

export const getOverview = () => api.get<AppResponse<OverviewDto>>('/api/v1/control-center/overview');
export const getSystemMaintenance = () =>
  api.get<AppResponse<SystemMaintenanceDto>>('/ops-api/system-status');
export const updateSystemMaintenance = (request: { mode: SystemStatusMode; reasonCategory: string; reason: string; confirmationPassword: string }) =>
  api.put<AppResponse<SystemMaintenanceDto>>('/ops-api/system-status', request);
export const getTenants = () => api.get<AppResponse<TenantDto[]>>('/api/v1/control-center/tenants');
export const createTenant = (request: CreateTenantRequest) =>
  api.post<AppResponse<TenantCreatedDto>>('/api/v1/control-center/tenants', request);
export const updateTenantStatus = (tenantId: number, status: number) =>
  api.put<AppResponse<null>>(`/api/v1/control-center/tenants/${tenantId}/status`, { status });
export const getTenant = (tenantId: number) =>
  api.get<AppResponse<TenantDetailsDto>>(`/api/v1/control-center/tenants/${tenantId}`);
export const updateTenant = (tenantId: number, request: UpdateTenantRequest) =>
  api.put<AppResponse<null>>(`/api/v1/control-center/tenants/${tenantId}`, request);
export const getTenantFeatures = (tenantId: number) =>
  api.get<AppResponse<TenantFeaturesDto>>(`/api/v1/control-center/tenants/${tenantId}/features`);
export const updateTenantFeatures = (tenantId: number, request: TenantFeaturesDto) =>
  api.put<AppResponse<TenantFeaturesDto>>(`/api/v1/control-center/tenants/${tenantId}/features`, request);
export const archiveTenant = (tenantId: number) =>
  api.delete<AppResponse<null>>(`/api/v1/control-center/tenants/${tenantId}`);
export const getTerminals = () => api.get<AppResponse<TerminalDto[]>>('/ops-api/terminals');
export const updateTerminal = (terminalId: number, request: UpdateAdminTerminalRequest) =>
  api.put<AppResponse<null>>(`/api/v1/admin/terminals/${terminalId}`, request);
export const deleteTerminal = (terminalId: number) =>
  api.delete<AppResponse<null>>(`/api/v1/admin/terminals/${terminalId}`);

export const getUsers = () => api.get<AppResponse<AdminUserDto[]>>('/api/v1/admin/users');
export const confirmUserEmail = (userId: number) =>
  api.post<AppResponse<null>>(`/api/v1/admin/users/${userId}/confirm-email`);
export const setUserPassword = (userId: number, password: string) =>
  api.post<AppResponse<null>>(`/api/v1/admin/users/${userId}/set-password`, { password });
export const updateUser = (userId: number, request: UpdateAdminUserRequest) =>
  api.put<AppResponse<null>>(`/api/v1/admin/users/${userId}`, request);
export const deleteUser = (userId: number) =>
  api.delete<AppResponse<null>>(`/api/v1/admin/users/${userId}`);
export const getCompanies = () => api.get<AppResponse<AdminCompanyDto[]>>('/api/v1/admin/companies');
export const updateCompany = (companyId: number, request: UpdateAdminCompanyRequest) =>
  api.put<AppResponse<null>>(`/api/v1/admin/companies/${companyId}`, request);
export const deleteCompany = (companyId: number) =>
  api.delete<AppResponse<null>>(`/api/v1/admin/companies/${companyId}`);

export const getEplIntegrations = () =>
  api.get<AppResponse<EplTenantDto[]>>('/ops-api/epl');
export const getEdoOperators = () =>
  api.get<AppResponse<EdoOperatorDto[]>>('/ops-api/epl/operators');
export const createEplCompany = (request: CreateEplCompanyRequest) =>
  api.post<AppResponse<{ companyId: number }>>('/ops-api/epl/companies', request);
export const updateEplCompanyOperator = (companyId: number, edoOperatorId: number | null) =>
  api.put<AppResponse<{ companyId: number; edoOperatorId: number | null }>>(
    `/ops-api/epl/companies/${companyId}/operator`,
    { edoOperatorId },
  );
export const saveEplIntegration = (tenantId: number, request: SaveEplIntegrationRequest) =>
  api.put<AppResponse<AstralIntegrationDto>>(`/ops-api/epl/tenants/${tenantId}`, request);
export const testEplIntegration = (tenantId: number) =>
  api.post<AppResponse<AstralConnectionTestDto>>(`/ops-api/epl/tenants/${tenantId}/test`);

export const getQueueSettings = () =>
  api.get<AppResponse<QueueSettingDto[]>>('/api/v1/admin/queue-settings');
export const updateQueueSettings = (tenantId: number, request: Omit<QueueSettingDto, 'tenantId'>) =>
  api.put<AppResponse<QueueSettingDto>>(`/ops-api/queue-settings/${tenantId}`, request);

export const getActivityCenter = () =>
  api.get<AppResponse<ActivityCenterDto>>('/ops-api/activity-center');

export interface CheckupFilters {
  status?: string;
  tenantId?: string;
  companyId?: string;
  search?: string;
  from?: string;
  to?: string;
}

export const getOperationsDashboard = () =>
  api.get<AppResponse<OperationsDashboardDto>>('/ops-api/dashboard');
export const getOperationsCheckups = (filters: CheckupFilters) =>
  api.get<AppResponse<OperationsCheckupDto[]>>('/ops-api/checkups', { params: filters });
export const getOperationsCheckup = (id: number) =>
  api.get<AppResponse<OperationsCheckupDetailsDto>>(`/ops-api/checkups/${id}`);
export const requeueOperationsCheckup = (id: number) =>
  api.post<AppResponse<{ id: number; status: number; statusLabel: string }>>(`/ops-api/checkups/${id}/requeue`);
export const getDocumentTemplates = () =>
  api.get<AppResponse<DocumentTemplateDto[]>>('/ops-api/templates');
export const getDocumentTemplate = (tenantId: number) =>
  api.get<AppResponse<DocumentTemplateDto>>(`/ops-api/templates/${tenantId}`);
export const copySystemDocumentTemplate = (tenantId: number) =>
  api.post<AppResponse<{ tenantId: number; templateId: number }>>(`/ops-api/templates/${tenantId}/copy-system`);
export const updateDocumentTemplate = (tenantId: number, headerHtml: string, footerHtml: string) =>
  api.put<AppResponse<{ tenantId: number; templateId: number }>>(`/ops-api/templates/${tenantId}`, { headerHtml, footerHtml });
export const getAuditEvents = () =>
  api.get<AppResponse<AuditEventDto[]>>('/ops-api/audit');

// ─── Quick Onboarding ────────────────────────────────────────

export const quickCreateTenant = (request: QuickCreateTenantRequest) =>
  api.post<AppResponse<{ tenantId: number; name: string }>>('/ops-api/tenants/quick-create', request);

// ─── Terminal Health ─────────────────────────────────────────

export const getTerminalHealth = (terminalId: number) =>
  api.get<AppResponse<TerminalHealthDto>>(`/ops-api/terminals/${terminalId}/health`);

export const getTerminalLogs = (terminalId: number, lines = 100) =>
  api.get<AppResponse<{ terminal: { id: number; name: string; tenantId: number }; logs: TerminalLogEntry[] }>>(`/ops-api/terminals/${terminalId}/logs`, { params: { lines } });

export const sendTerminalCommand = (terminalId: number, command: string, params?: Record<string, unknown>) =>
  api.post<AppResponse<{ sent: boolean; command: string; terminalId: number; terminalName: string; online: boolean }>>(`/ops-api/terminals/${terminalId}/command`, { command, params });

export const getMeshCentralDevices = () =>
  api.get<AppResponse<MeshDeviceDto[]>>('/ops-api/meshcentral/devices');

export const getMeshCentralFleetSummary = () =>
  api.get<AppResponse<MeshFleetSummaryDto>>('/ops-api/meshcentral/fleet-summary');

export const getMeshCentralDeviceHealth = (nodeId: string) =>
  api.get<AppResponse<MeshDeviceHealthDto>>('/ops-api/meshcentral/device-health', { params: { nodeId } });

export const refreshMeshCentralInventory = () =>
  api.post<AppResponse<{ updated: number }>>('/ops-api/meshcentral/refresh');

export const runMeshCentralDiagnostics = (nodeId: string) =>
  api.post<AppResponse<TerminalDiagnosticsDto>>('/ops-api/meshcentral/diagnostics', { nodeId });

export const getMeshCentralLogs = (nodeId: string, lines = 200) =>
  api.post<AppResponse<{ device: { nodeId: string; name: string }; logs: TerminalLogEntry[] }>>('/ops-api/meshcentral/logs', { nodeId, lines });

export const searchMeshCentralExamHistory = (nodeId: string, filters: TerminalExamHistorySearchDto = {}, limit = 200, offset = 0) =>
  api.post<AppResponse<{ device: { nodeId: string; name: string }; items: TerminalExamHistoryItemDto[]; limit: number; offset: number; total: number }>>('/ops-api/meshcentral/exam-history/search', { nodeId, ...filters, limit, offset });

export const getMeshCentralExamDetails = (nodeId: string, examNumber: string) =>
  api.post<AppResponse<TerminalExamDetailsDto>>('/ops-api/meshcentral/exam-history/details', { nodeId, examNumber });

export const printMeshCentralExam = (nodeId: string, examNumber: string) =>
  api.post<AppResponse<{ operation: TerminalExamPrintOperationDto; exam: TerminalExamDetailsDto & { pdfPath: string; printer: string; printOutput: string } }>>('/ops-api/meshcentral/exam-history/print', { nodeId, examNumber });

export const getMeshCentralExamPrintOperations = (nodeId: string) =>
  api.get<AppResponse<TerminalExamPrintOperationDto[]>>('/ops-api/meshcentral/exam-history/print-operations', { params: { nodeId } });

export const getMeshCentralTonometer = (nodeId: string) =>
  api.get<AppResponse<TonometerStateDto>>('/ops-api/meshcentral/tonometer', { params: { nodeId } });

export const discoverMeshCentralTonometers = (nodeId: string) =>
  api.post<AppResponse<BluetoothTonometerDto[]>>('/ops-api/meshcentral/tonometer/discovery', { nodeId });

export const provisionMeshCentralTonometer = (nodeId: string, deviceAddress: string, confirmation: string) =>
  api.post<AppResponse<TonometerOperationDto>>('/ops-api/meshcentral/tonometer/provision', { nodeId, deviceAddress, confirmation });

export const testMeshCentralTonometer = (nodeId: string) =>
  api.post<AppResponse<TonometerTestDto>>('/ops-api/meshcentral/tonometer/test', { nodeId });

export const getMeshCentralTonometerOperations = (nodeId: string) =>
  api.get<AppResponse<TonometerOperationDto[]>>('/ops-api/meshcentral/tonometer/operations', { params: { nodeId } });

export const getMeshCentralTonometerOperation = (operationId: number) =>
  api.get<AppResponse<TonometerOperationDto>>(`/ops-api/meshcentral/tonometer/operations/${operationId}`);

export const createMeshCentralRemoteSession = (nodeId: string, protocol: MeshRemoteProtocol) =>
  api.post<AppResponse<MeshRemoteSessionDto>>('/ops-api/meshcentral/remote-sessions', { nodeId, protocol });

export const getMeshCentralCommands = () =>
  api.get<AppResponse<MeshCommandDefinitionDto[]>>('/ops-api/meshcentral/commands');

export const getMeshCentralCommandJobs = (nodeId: string) =>
  api.get<AppResponse<MeshCommandJobDto[]>>('/ops-api/meshcentral/command-jobs', { params: { nodeId } });

export const getMeshCentralCommandJob = (jobId: number) =>
  api.get<AppResponse<MeshCommandJobDto>>(`/ops-api/meshcentral/command-jobs/${jobId}`);

export const createMeshCentralCommandJob = (nodeId: string, commandId: string, confirmation: string) =>
  api.post<AppResponse<MeshCommandJobDto>>('/ops-api/meshcentral/command-jobs', { nodeId, commandId, confirmation });

export const deleteMeshCentralCommandJobs = (nodeId: string) =>
  api.delete<AppResponse<{ deleted: number }>>('/ops-api/meshcentral/command-jobs', { data: { nodeId } });

export const deleteMeshCentralCommandJob = (jobId: number) =>
  api.delete<AppResponse<{ deleted: number }>>(`/ops-api/meshcentral/command-jobs/${jobId}`);

export const getSupportScenarios = () =>
  api.get<AppResponse<SupportScenarioDto[]>>('/ops-api/support/scenarios');

export const getMedscreenerBackups = (nodeId: string) =>
  api.get<AppResponse<MedscreenerBackupDto[]>>('/ops-api/support/medscreener-backups', { params: { nodeId } });

export const getSupportJobs = (nodeId: string) =>
  api.get<AppResponse<SupportJobDto[]>>('/ops-api/support/jobs', { params: { nodeId } });

export const getSupportJob = (jobId: number) =>
  api.get<AppResponse<SupportJobDto>>(`/ops-api/support/jobs/${jobId}`);

export const createSupportJob = (request: { nodeId: string; scenarioId: string; confirmation?: string; parameters?: Record<string, unknown>; idempotencyKey: string }) =>
  api.post<AppResponse<SupportJobDto>>('/ops-api/support/jobs', request);

export const createAiAgentCommandJob = (request: { nodeId: string; confirmation: string; goal: string; rationale: string; commands: Array<{ label: string; command: string; expectedResult: string }>; idempotencyKey: string }) =>
  api.post<AppResponse<SupportJobDto>>('/ops-api/ai/command-jobs', request);

export const cancelSupportJob = (jobId: number) =>
  api.post<AppResponse<SupportJobDto>>(`/ops-api/support/jobs/${jobId}/cancel`);

export const deleteSupportJobs = (nodeId: string) =>
  api.delete<AppResponse<{ deleted: number }>>('/ops-api/support/jobs', { data: { nodeId } });

export const deleteSupportJob = (jobId: number) =>
  api.delete<AppResponse<{ deleted: number }>>(`/ops-api/support/jobs/${jobId}`);

export const getSupportArtifact = (jobId: number, artifactId: number) =>
  api.get<AppResponse<{ id: number; name: string; contentType: string; hash: string; size: number; content: unknown }>>(`/ops-api/support/jobs/${jobId}/artifacts/${artifactId}`);

export const getMeshCentralPowerOperations = (nodeId: string) =>
  api.get<AppResponse<MeshPowerOperationDto[]>>('/ops-api/meshcentral/power-operations', { params: { nodeId } });

export const getMeshCentralPowerOperation = (operationId: number) =>
  api.get<AppResponse<MeshPowerOperationDto>>(`/ops-api/meshcentral/power-operations/${operationId}`);

export const createMeshCentralPowerOperation = (nodeId: string, operation: MeshPowerAction, confirmation?: string) =>
  api.post<AppResponse<MeshPowerOperationDto>>('/ops-api/meshcentral/power-operations', { nodeId, operation, confirmation });

export const getMeshCentralConfigurationBackups = (nodeId: string) =>
  api.get<AppResponse<ConfigurationBackupDto[]>>('/ops-api/meshcentral/configuration-backups', { params: { nodeId } });

export const backupMeshCentralConfiguration = (nodeId: string) =>
  api.post<AppResponse<ConfigurationBackupDto>>('/ops-api/meshcentral/configuration-backups', { nodeId });

export const getMeshCentralConfigurationBackup = (nodeId: string, backupId: number) =>
  api.get<AppResponse<ConfigurationBackupContentDto>>(`/ops-api/meshcentral/configuration-backups/${backupId}`, { params: { nodeId } });

export const compareMeshCentralConfigurationBackups = (nodeId: string, from: number, to: number) =>
  api.get<AppResponse<ConfigurationDiffDto>>('/ops-api/meshcentral/configuration-diff', { params: { nodeId, from, to } });

export const getMeshCentralLiveConfiguration = (nodeId: string) =>
  api.get<AppResponse<ConfigurationLiveDto>>('/ops-api/meshcentral/configuration-live', { params: { nodeId } });

export const getConfigurationTemplates = () =>
  api.get<AppResponse<ConfigurationTemplateDto[]>>('/ops-api/meshcentral/configuration-templates');

export const getConfigurationTemplate = (templateId: number) =>
  api.get<AppResponse<ConfigurationTemplateDto>>(`/ops-api/meshcentral/configuration-templates/${templateId}`);

export const createConfigurationTemplate = (request: { name: string; description?: string; content: string | Record<string, unknown> }) =>
  api.post<AppResponse<ConfigurationTemplateDto>>('/ops-api/meshcentral/configuration-templates', request);

export const deleteConfigurationTemplate = (templateId: number) =>
  api.delete<AppResponse<{ deleted: boolean }>>(`/ops-api/meshcentral/configuration-templates/${templateId}`);

export const previewMeshCentralConfiguration = (request: { nodeId: string; content?: string | Record<string, unknown>; templateId?: number }) =>
  api.post<AppResponse<ConfigurationPreviewDto>>('/ops-api/meshcentral/configuration-preview', request);

export const applyMeshCentralConfiguration = (request: { nodeId: string; content?: string | Record<string, unknown>; templateId?: number; expectedHash: string; reason: string; acknowledgeCritical: boolean }) =>
  api.post<AppResponse<ConfigurationPreviewDto & { hash: string; backupId: number }>>('/ops-api/meshcentral/configuration-apply', request);

export const linkTerminalToMeshCentral = (terminalId: number, nodeId: string) =>
  api.put<AppResponse<MeshDeviceDto>>(`/ops-api/terminals/${terminalId}/meshcentral-link`, { nodeId });

export const runTerminalDiagnostics = (terminalId: number) =>
  api.post<AppResponse<TerminalDiagnosticsDto>>(`/ops-api/terminals/${terminalId}/diagnostics`);

export const getConfigurationBackups = (terminalId: number) =>
  api.get<AppResponse<ConfigurationBackupDto[]>>(`/ops-api/terminals/${terminalId}/configuration-backups`);

export const backupTerminalConfiguration = (terminalId: number) =>
  api.post<AppResponse<ConfigurationBackupDto>>(`/ops-api/terminals/${terminalId}/configuration-backups`);

export const getConfigurationBackup = (terminalId: number, backupId: number) =>
  api.get<AppResponse<ConfigurationBackupContentDto>>(`/ops-api/terminals/${terminalId}/configuration-backups/${backupId}`);

export const compareConfigurationBackups = (terminalId: number, from: number, to: number) =>
  api.get<AppResponse<ConfigurationDiffDto>>(`/ops-api/terminals/${terminalId}/configuration-diff`, { params: { from, to } });

export const bulkUpdateTerminals = (terminalIds: number[], action: string, params?: Record<string, unknown>) =>
  api.post<AppResponse<{ affected: number; action: string; terminals: { id: number; name: string }[] }>>('/ops-api/terminals/bulk-update', { terminalIds, action, params });

// ─── RabbitMQ ────────────────────────────────────────────────

export const getRabbitMQOverview = () =>
  api.get<AppResponse<RabbitMQOverview>>('/ops-api/rabbitmq/overview');

export const getRabbitMQQueues = () =>
  api.get<AppResponse<RabbitMQQueue[]>>('/ops-api/rabbitmq/queues');

export const purgeRabbitMQQueue = (queueName: string) =>
  api.post<AppResponse<{ purged: boolean; queue: string }>>(`/ops-api/rabbitmq/queues/${encodeURIComponent(queueName)}/purge`);

export const deleteRabbitMQQueue = (queueName: string) =>
  api.post<AppResponse<{ deleted: boolean; queue: string }>>(`/ops-api/rabbitmq/queues/${encodeURIComponent(queueName)}/delete`);

export const getRabbitMQConnections = () =>
  api.get<AppResponse<RabbitMQConnection[]>>('/ops-api/rabbitmq/connections');

// ─── S3 / MinIO ─────────────────────────────────────────────

export const getS3Buckets = () =>
  api.get<AppResponse<S3Bucket[]>>('/ops-api/s3/buckets');

export const getS3BucketObjects = (bucketName: string, prefix = '', marker = '') =>
  api.get<AppResponse<S3ObjectList>>(`/ops-api/s3/buckets/${encodeURIComponent(bucketName)}/objects`, { params: { prefix, marker } });

export const deleteS3Object = (bucketName: string, objectKey: string) =>
  api.delete<AppResponse<{ deleted: boolean; bucket: string; key: string }>>(`/ops-api/s3/buckets/${encodeURIComponent(bucketName)}/objects/${encodeURIComponent(objectKey)}`);

export const getS3Stats = () =>
  api.get<AppResponse<S3Stats>>('/ops-api/s3/stats');

// ─── Analytics ───────────────────────────────────────────────

export const getCheckupsByDay = (days = 30) =>
  api.get<AppResponse<AnalyticsDay[]>>('/ops-api/analytics/checkups-by-day', { params: { days } });

export const getTenantsActivity = () =>
  api.get<AppResponse<TenantActivity[]>>('/ops-api/analytics/tenants-activity');

export const getTerminalUptime = () =>
  api.get<AppResponse<TerminalUptime[]>>('/ops-api/analytics/terminal-uptime');

export const getMedicLoad = () =>
  api.get<AppResponse<MedicLoad[]>>('/ops-api/analytics/medic-load');

export const getApiError = (error: any) =>
  error?.response?.data?.message || error?.message || 'Не удалось выполнить запрос';
